import { useCallback, useEffect, useState } from "react";
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { RootState } from '../../../../../store/store';
import { useTableData } from './usePunchListsTableData';
import { FaSearch } from "react-icons/fa";
import { MdOutlineClear } from "react-icons/md";
import { paginationLimits, PunchListViewOptions } from "../../../../../Constants/Consts.ts";
import { PunchlistApiType } from "../../../../../types/PunchList/PunchListSchema.ts";
import Pagination from "../../../../../components/Pagination/Pagination.tsx";
import usePagination from "../../../../../Helpers/CustomHooks/usePagination.ts";
import Dropdown from "../../../../../components/CoreComponents/Select.tsx";
import { getLocalStorageItem, setLocalStorageItem } from "../../../../../Helpers/Functions.ts";
import { twMerge } from "tailwind-merge";

interface IProps {
    filteredProjectPunchLists: PunchlistApiType[]
}

export default function PunchlistTable({ filteredProjectPunchLists }: IProps) {
    const { disciplineId, listStatus, assignationFilter } = useParams()
    let { viewType } = useParams();
    viewType = viewType || (disciplineId ? PunchListViewOptions.DISCIPLINE.name : viewType);
    const [punchlistsByViewType, setPunchlistsByViewType] = useState<PunchlistApiType[]>([]);
    const navigate = useNavigate()
    const { ProjectPunchLists } = useSelector((state: RootState) => state.PunchList)
    const { SelectedUser } = useSelector(
        (state: RootState) => state.SelectedUser
    );

    const isUserFilteringFromPuchListStatus = filteredProjectPunchLists && filteredProjectPunchLists.length >= 0;

    useEffect(() => {
        if (!isUserFilteringFromPuchListStatus) {
            const tempPunchLists = [];
            if (viewType === PunchListViewOptions.DISCIPLINE.name && disciplineId) {
                tempPunchLists.push(...ProjectPunchLists.filter(pl => Number(pl[assignationFilter]) === Number(disciplineId)))
            } else if (viewType === PunchListViewOptions.ALL_PUNCH_LISTS.name) {
                tempPunchLists.push(...ProjectPunchLists)
            } else if (viewType === PunchListViewOptions.MY_PUNCH_LISTS.name) {
                tempPunchLists.push(...ProjectPunchLists.filter(pl => pl.identifiedBy === SelectedUser.email || pl.identifiedBy === SelectedUser.kionEmail))
            } else if (viewType === PunchListViewOptions.ERM_RELEVANT.name) {
                tempPunchLists.push(...ProjectPunchLists.filter(pl => pl.ermRelevant))
            }
            setPunchlistsByViewType(tempPunchLists);
        }
    }, [isUserFilteringFromPuchListStatus, viewType]);

    const punchlists = punchlistsByViewType.filter(p => String(p.statusId) == listStatus)
        .sort((a, b) => Date.parse(b.dateIdentified) - Date.parse(a.dateIdentified))

    const currePresentedPunchLists = isUserFilteringFromPuchListStatus ? filteredProjectPunchLists : punchlists;

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        search, setSearch,
        handleUpdateSearchTermInLocalStorage,
        handleClearSearchTermInLocalStorage
    } = useTableData(currePresentedPunchLists, 'punchlist')


    const [seletedPaginationLimit, setSelectedPaginationLimit] = useState(paginationLimits[0])
    const { currentPage, totalPages, currentRows, handleNextPage, handlePreviousPage } =
        usePagination(rows, seletedPaginationLimit);

    const [searchInspectionTask, setSearchInspectionTask] = useState("");
    const filteredPunchlistsId = punchlists.map(punchlist => punchlist.punchListUuid)
    const paginationLimitsOptions = paginationLimits.filter(item => +item < rows.length || item === 20).map(num => ({ text: num.toString(), value: num }))
    const resetSearchInspectionTask = () => setSearchInspectionTask("");

    useEffect(() => {
        const cachedPaginationLimit = getLocalStorageItem('punchlist_pagination_limit');

        if (cachedPaginationLimit) setSelectedPaginationLimit(+cachedPaginationLimit)
        else setLocalStorageItem('punchlist_pagination_limit', paginationLimits[0]);
        handleClearSearchTermInLocalStorage();

    }, [])

    // Clear search when switching between statuses
    useEffect(() => {
        setSearch("");
        handleClearSearchTermInLocalStorage();
    }, [listStatus])

    const handleChangePaginationLimit = (selectedOptions) => {
        setSelectedPaginationLimit(+selectedOptions.value)
        setLocalStorageItem('punchlist_pagination_limit', +selectedOptions.value);
    }

    const handleClickRow = useCallback((punchListUuid: string) => {
        setLocalStorageItem('lastClickedRowId', punchListUuid)
        handleUpdateSearchTermInLocalStorage()
        navigate(`/PunchList/DetailsPage/${punchListUuid}`, { state: filteredPunchlistsId })
    }, [filteredPunchlistsId])

    return (
        <div className="overflow-x-auto">
            {!isUserFilteringFromPuchListStatus &&
                <div className="flex border rounded-lg my-2">
                    <div className="border-r rounded-r-lg">
                        <span
                            className="flex px-5 items-center text-sm font-medium bg-gray-50 py-3 dark:bg-gray-900 dark:text-gray-200">
                            <FaSearch />
                            <span className="ml-2">Search</span>
                        </span>
                    </div>
                    <div className="flex justify-end relative w-full">
                        <input
                            value={search ?? ""}
                            className='border px-2 h-full w-full'
                            id="search"
                            onChange={(e) => {
                                setSearch(e.target.value);
                                handleUpdateSearchTermInLocalStorage(e.target.value)
                            }}
                            type="text"
                            placeholder="Search any field below"
                        />
                        <div className="border-l bg-gray-50">
                            <button type="button" className="py-3" title="Clear" onClick={resetSearchInspectionTask}
                                hidden={searchInspectionTask === ""}>
                                <MdOutlineClear />
                            </button>
                        </div>
                    </div>
                    <div className="w-[150px] h-[44px]">
                        <Dropdown
                            customStyles="!min-h-[44px]"
                            options={paginationLimitsOptions}
                            selected={seletedPaginationLimit}
                            onChangeEvent={(selectedOptions) => handleChangePaginationLimit(selectedOptions)}
                        />
                    </div>
                </div>
            }
            <table {...getTableProps()} className="text-sm text-left">
                <thead className="border-b font-medium dark:border-neutral-500">
                    {headerGroups.map(headerGroup => {
                        const { key, ...restProps } = headerGroup.getHeaderGroupProps()
                        return (
                            <tr key={key} {...restProps}>
                                {headerGroup.headers.map(column => {
                                    const { key, ...restProps } = column.getHeaderProps(column.getSortByToggleProps())
                                    return (
                                        <th
                                            className="px-6 py-4 whitespace-nowrap overflow-hidden truncate"
                                            key={key} {...restProps}>
                                            {column.render("Header")}
                                            <span>
                                                {column.isSorted
                                                    ? column.isSortedDesc
                                                        ? ' 🔽'
                                                        : ' 🔼'
                                                    : ''}
                                            </span>
                                        </th>
                                    )
                                })}
                            </tr>
                        )
                    })}
                </thead>
                <tbody {...getTableBodyProps()}>
                    {currentRows.map((row, i) => {
                        prepareRow(row);
                        const { key, ...restProps } = row.getRowProps();
                        const lastClickedId = getLocalStorageItem("lastClickedRowId");
                        const isHighlighted = lastClickedId === row.original.punchListUuid;
                        const classNames = twMerge(
                            "cursor-pointer hover:bg-orange-1000 hover:text-white",
                            i % 2 == 0 ? "border-b bg-white dark:border-neutral-500 dark:bg-neutral-600"
                                : "border-b bg-neutral-100 dark:border-neutral-500 dark:bg-neutral-700",
                            isHighlighted ? " bg-orange-200 dark:bg-orange-1000 hover:bg-orange-200" : ""
                        )
                        return (
                            <tr
                                key={key}
                                onClick={() => handleClickRow(row.original.punchListUuid)}
                                style={{ width: "85vw" }}
                                className={classNames}
                                {...restProps}>
                                {row.cells.map((cell, index) => {
                                    const { key, ...restProps } = cell.getCellProps()
                                    const isIconColumn = index === 0;
                                    return (
                                        <td className={isIconColumn ? "px-3 py-4" : "px-6 py-4"} key={key} {...restProps}>
                                            <div className="flex">{cell.render("Cell")} </div>
                                        </td>
                                    );
                                })}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                handleNextPage={handleNextPage}
                handlePreviousPage={handlePreviousPage}
            />
        </div>
    )
}
